/**
 * Per-room horizontal capture-band timeline for a given day. Each room gets
 * a track spanning the clinic's active window; "flowing" segments (audio
 * actively being captured) render as solid bars, "gap" segments are just the
 * bare muted track. On today's timeline a live "now" line and per-room "Live"
 * badges reflect whether capture is happening at this moment.
 */
import { useSessionStore } from "@auth/sessionStore";
import type {
  CaptureSegment,
  RoomCaptureTimeline,
  VisitPresence,
} from "@capture/captureClient";
import { useCaptureTimeline } from "@capture/captureQueries";
import { EmptyNote, LabelTape, Swipe } from "@scribenote/brian-design-system";
import { formatTime } from "@utils/format-date";
import { useNow } from "@utils/use-now";
import { addHours } from "date-fns";
import { useState } from "react";

// The lane the day is drawn in. `bg-recess` alone is 1.09:1 against the page,
// so an empty room read as nothing at all (WEB-3346); `faint` is the lightest
// ink that clears 3:1 on paper (3.28:1), which is what a graphical boundary
// owes. An inset ring, not a border: a border insets the padding box the bars
// and ticks resolve their percentages against, while the now line, the hour
// labels and BarTooltip resolve against the track's full width - a 1px drift at
// the edges of a 2px line. The ring is a box-shadow, so `inset-y-0` bars paint
// over it; the wash leaves it readable and an opaque bar is its own boundary.
const TRACK = "inset-ring-1 inset-ring-faint bg-recess";

// The hour grid, drawn opaque. It rules the empty track, which is where it
// carries position against the hour labels and where it owes 3:1: `faint`
// measures 3.00:1 there, against 1.16:1 for `rule` at 60% and 1.65:1 for
// `faint` at half strength, both invisible. The grid paints over the presence
// wash (see the DOM order in RoomRow), where it falls to 2.48:1 resting and
// 2.08:1 hovered - deliberately, since a bar's own edges carry position under
// it, and the lightest ink clearing 3:1 on the wash is annotation-weight, loud
// enough to rule the whole day over the bars it is meant to sit behind.
const HAIRLINE = "border-faint";

/**
 * Someone was in the room: indigo held far back, the accent used as a wash over
 * the track rather than a field of its own. The wash cannot carry the shape by
 * itself (1.21:1 on the track, and the accent would have to become a field to
 * fix that), so the deep accent draws the block's edges and the fill stays a
 * wash. The edge clears 3:1 on each ground it is actually drawn against: 5.36:1
 * on the bare track, 4.43:1 on the resting wash, 3.72:1 on the hover wash.
 * One expression, shared by the bars and the legend swatch that explains them,
 * so the two cannot drift apart.
 */
const PRESENCE_FILL =
  "color-mix(in srgb, var(--color-accent) 16%, transparent)";
const PRESENCE_FILL_HOVER =
  "color-mix(in srgb, var(--color-accent) 30%, transparent)";
// The ink only; which sides get an edge is the call site's (an open-ended stay
// draws no end edge). The legend swatch has to pick the same sides as a closed
// bar, or the key shows a shape the track never draws.
const PRESENCE_EDGE = "border-accent-deep";

const TICK_HOURS = 2;

// The room-name label column is `w-36` (9rem) plus a `gap-2` (0.5rem) before
// the track starts. Every row and the header tick row share this exact
// layout so a percentage within a track lines up across rows; the "now"
// line overlay (which spans outside any single track) needs that same
// offset expressed as a raw length to compute its own `left`.
const LABEL_COL_REM = "9.5rem";

/** `t` (epoch ms) as a percentage of [windowStart, windowEnd]. */
const makePct =
  (windowStart: number, span: number) =>
  (t: number): number =>
    ((t - windowStart) / span) * 100;

/** Hour ticks from the first whole hour ≥ `windowStart`, every `TICK_HOURS`. */
const hourTicks = (windowStart: number, windowEnd: number): number[] => {
  let tick = new Date(windowStart);
  tick.setMinutes(0, 0, 0);
  if (tick.getTime() < windowStart) tick = addHours(tick, 1);

  const ticks: number[] = [];
  for (let t = tick; t.getTime() <= windowEnd; t = addHours(t, TICK_HOURS)) {
    ticks.push(t.getTime());
  }
  return ticks;
};

const isFlowing = (
  s: CaptureSegment,
): s is CaptureSegment & { kind: "flowing" } => s.kind === "flowing";

/**
 * One rendered presence bar: the union of overlapping stays (multi-pet visits
 * share a room), so bars never stack and the tooltip lists every patient.
 * Open-ended means at least one merged stay has no `leftAt`: the bar runs to
 * the window end and fades out to read as "ongoing".
 */
interface PresenceBlock {
  start: number;
  end: number;
  openEnded: boolean;
  stays: VisitPresence[];
}

const presenceBlocks = (
  stays: VisitPresence[],
  windowStart: number,
  windowEnd: number,
): PresenceBlock[] => {
  const intervals = stays
    .map((stay) => ({
      // Stays arrive un-clamped (they may cross midnight); clamp to the axis.
      start: Math.max(new Date(stay.enteredAt).getTime(), windowStart),
      end: stay.leftAt
        ? Math.min(new Date(stay.leftAt).getTime(), windowEnd)
        : windowEnd,
      openEnded: stay.leftAt === null,
      stay,
    }))
    .filter((i) => i.end > i.start)
    .sort((a, b) => a.start - b.start);

  const blocks: PresenceBlock[] = [];
  for (const interval of intervals) {
    const last = blocks[blocks.length - 1];
    if (last && interval.start <= last.end) {
      last.end = Math.max(last.end, interval.end);
      last.openEnded = last.openEnded || interval.openEnded;
      last.stays.push(interval.stay);
    } else {
      blocks.push({
        start: interval.start,
        end: interval.end,
        openEnded: interval.openEnded,
        stays: [interval.stay],
      });
    }
  }
  return blocks;
};

const presenceLines = (block: PresenceBlock): string[] =>
  block.stays.map(
    (stay) =>
      `${stay.patientName ?? "Unknown patient"} ${formatTime(stay.enteredAt)} – ${
        stay.leftAt ? formatTime(stay.leftAt) : "still in room"
      }`,
  );

/**
 * Hover tooltip for a timeline bar, floated above the row at the bar's
 * midpoint. Lives at row level (not inside the track) because the track
 * clips its children (`overflow-hidden`); `fraction` is the midpoint's share
 * of the track width, clamped so the tooltip never pokes past the page edge.
 */
const BarTooltip = ({
  fraction,
  lines,
}: {
  fraction: number;
  lines: string[];
}) => {
  const clamped = Math.min(Math.max(fraction, 0.06), 0.94);
  return (
    <div
      className="pointer-events-none absolute bottom-full z-dropdown mb-1 -translate-x-1/2 rounded-paper border border-rule bg-card px-2 py-1 text-xs whitespace-nowrap text-ink shadow-float"
      style={{
        left: `calc(${LABEL_COL_REM} + (100% - ${LABEL_COL_REM}) * ${clamped.toFixed(4)})`,
      }}
    >
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  );
};

type HoveredBar = { kind: "presence" | "flowing"; index: number } | null;

const containsNow = (segments: CaptureSegment[], now: number): boolean =>
  segments.some(
    (s) =>
      isFlowing(s) &&
      new Date(s.start).getTime() <= now &&
      now <= new Date(s.end).getTime(),
  );

export const CaptureTimeline = ({
  date,
  isToday,
}: {
  date: string;
  /** Whether `date` is clinic-today (the panel computes this in clinic tz). */
  isToday: boolean;
}) => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data } = useCaptureTimeline(sessionKey, date, isToday);

  // Window bounds and "now" are compared as epoch ms; labels are formatted in
  // the browser's local timezone via `formatTime`. If clinic timezone ever
  // diverges from the viewer's browser timezone, swap in `useClinic().timezone`
  // for label formatting.
  const windowStart = new Date(data.windowStart).getTime();
  const windowEnd = new Date(data.windowEnd).getTime();
  const span = windowEnd - windowStart;

  const now = useNow();
  const nowInWindow = isToday && now >= windowStart && now <= windowEnd;

  if (span <= 0) {
    return <EmptyNote>No timeline for this day.</EmptyNote>;
  }

  if (data.rooms.length === 0) {
    return <EmptyNote>No active rooms.</EmptyNote>;
  }

  const pct = makePct(windowStart, span);
  const ticks = hourTicks(windowStart, windowEnd);
  // `left = labelColumn + fraction * remainingWidth`: a length multiplied by
  // a plain number is valid `calc()`; mixing two percentages directly isn't.
  const nowLineLeft = `calc(${LABEL_COL_REM} + (100% - ${LABEL_COL_REM}) * ${(
    pct(now) / 100
  ).toFixed(6)})`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <div className="w-36 shrink-0" />
        <div className="relative h-5 flex-1">
          {ticks.map((tick) => {
            const left = pct(tick);
            // Edge ticks align inward instead of centering so labels never
            // hang past the track and force a horizontal scrollbar.
            const align =
              left < 2
                ? ""
                : left > 98
                  ? "-translate-x-full"
                  : "-translate-x-1/2";
            return (
              <span
                key={tick}
                style={{ left: `${left}%` }}
                className={`absolute font-mono text-fine-print whitespace-nowrap text-annotation ${align}`}
              >
                {formatTime(new Date(tick).toISOString())}
              </span>
            );
          })}
        </div>
      </div>

      <div className="relative flex flex-col gap-2">
        {nowInWindow && (
          <div
            data-testid="capture-now-line"
            className="pointer-events-none absolute inset-y-0 z-10 w-0.5 bg-ink-stopped"
            style={{ left: nowLineLeft }}
          />
        )}
        {data.rooms.map((room) => (
          <RoomRow
            key={room.roomUuid}
            room={room}
            pct={pct}
            ticks={ticks}
            now={now}
            nowInWindow={nowInWindow}
            windowStart={windowStart}
            windowEnd={windowEnd}
          />
        ))}
      </div>

      {/* The key to the track. A swatch here carries its word, so these are the
          inks the bars are drawn in rather than status washes. */}
      <div className="flex items-center gap-4 text-xs text-annotation">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-[1px] bg-ink-working" />
          Flowing
        </span>
        <span className="flex items-center gap-1.5">
          <span className={`size-2.5 rounded-[1px] ${TRACK}`} />
          Gap
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className={`size-2.5 rounded-[1px] border-x ${PRESENCE_EDGE}`}
            style={{ background: PRESENCE_FILL }}
          />
          In room
        </span>
      </div>
    </div>
  );
};

const RoomRow = ({
  room,
  pct,
  ticks,
  now,
  nowInWindow,
  windowStart,
  windowEnd,
}: {
  room: RoomCaptureTimeline;
  pct: (t: number) => number;
  ticks: number[];
  now: number;
  nowInWindow: boolean;
  windowStart: number;
  windowEnd: number;
}) => {
  const live = nowInWindow && containsNow(room.segments, now);
  const presence = presenceBlocks(room.stays, windowStart, windowEnd);
  const [hovered, setHovered] = useState<HoveredBar>(null);
  const flowing = room.segments.filter(isFlowing);

  const hoveredBlock =
    hovered?.kind === "presence" ? presence[hovered.index] : undefined;
  const hoveredSegment =
    hovered?.kind === "flowing" ? flowing[hovered.index] : undefined;

  return (
    <div
      data-testid={`capture-timeline-row-${room.roomUuid}`}
      className="relative flex items-center gap-2"
    >
      {hoveredBlock && (
        <BarTooltip
          fraction={(pct(hoveredBlock.start) + pct(hoveredBlock.end)) / 200}
          lines={presenceLines(hoveredBlock)}
        />
      )}
      {hoveredSegment && (
        <BarTooltip
          fraction={
            (pct(new Date(hoveredSegment.start).getTime()) +
              pct(new Date(hoveredSegment.end).getTime())) /
            200
          }
          lines={[
            `Flowing ${formatTime(hoveredSegment.start)} – ${formatTime(hoveredSegment.end)}`,
          ]}
        />
      )}
      <div className="flex w-36 shrink-0 items-center gap-1.5">
        {/* The room's label-maker tape. Names are free text, so the tape
            truncates rather than pushing the live marker out of the column. */}
        <LabelTape className="min-w-0 truncate">{room.roomName}</LabelTape>
        {live && (
          <Swipe
            tone="working"
            className="shrink-0 text-fine-print"
            data-testid={`capture-live-badge-${room.roomUuid}`}
          >
            Live
          </Swipe>
        )}
      </div>
      <div
        className={`relative h-6 flex-1 overflow-hidden rounded-paper ${TRACK}`}
      >
        {presence.map((block, i) => {
          const start = pct(block.start);
          const end = pct(block.end);
          const isHovered = hovered?.kind === "presence" && hovered.index === i;
          // Hover swaps in a stronger mix of the same accent the resting fill is
          // held back from.
          const color = isHovered ? PRESENCE_FILL_HOVER : PRESENCE_FILL;
          return (
            <div
              key={`${block.start}-${i}`}
              data-testid={`capture-presence-${room.roomUuid}-${i}`}
              onMouseEnter={() => setHovered({ kind: "presence", index: i })}
              onMouseLeave={() => setHovered(null)}
              // An open-ended stay is drawn with a start edge only: the fade
              // toward the window end is what says "no known end", and an edge
              // there would put a hard stop on a stay that has none.
              className={`absolute inset-y-0 transition-colors ${PRESENCE_EDGE} ${
                block.openEnded ? "border-l" : "border-x"
              }`}
              style={{
                left: `${start}%`,
                width: `${end - start}%`,
                // Open-ended stays fade out toward the window end to read as
                // "ongoing, no known end"; closed stays get a hard edge.
                background: block.openEnded
                  ? `linear-gradient(to right, ${color} 60%, transparent)`
                  : color,
              }}
            />
          );
        })}
        {ticks.map((tick) => (
          <div
            key={tick}
            style={{ left: `${pct(tick)}%` }}
            className={`pointer-events-none absolute inset-y-0 border-l ${HAIRLINE}`}
          />
        ))}
        {flowing.map((segment, i) => {
          const start = pct(new Date(segment.start).getTime());
          const end = pct(new Date(segment.end).getTime());
          const isHovered = hovered?.kind === "flowing" && hovered.index === i;
          return (
            <div
              key={`${segment.start}-${segment.end}`}
              onMouseEnter={() => setHovered({ kind: "flowing", index: i })}
              onMouseLeave={() => setHovered(null)}
              className={`absolute inset-y-0 bg-ink-working transition-[filter] ${isHovered ? "brightness-110" : ""}`}
              style={{ left: `${start}%`, width: `${end - start}%` }}
            />
          );
        })}
      </div>
    </div>
  );
};
